// team-v2.js — Team page event handlers and renderer extension.
// Tied tightly to the Team panel's static HTML in index.html.
//
// renderTeamV2 is called by src/team-page.js's renderTeamPageInner at
// the end of its render to layer on identity-level UX:
//   - seat counter (reads /v1/billing/status)
//   - admin-only invite-form visibility gate
//   - pending invitations panel + revoke + resend
//
// The big delegated click handler covers, in order:
//   1. team-invite-send  — Email-invite submit with the rich
//                          seat-cap 402 → "buy more seats" /
//                          "upgrade tier" flow
//   2. data-inv-resend   — Resend a pending invitation
//   3. data-inv-revoke   — Revoke a pending invitation
//   4. CLI invite section collapse toggle
//
// Plus a second click handler covering legacy team-management UX:
//   - team-show-create / team-show-join  (form toggles)
//   - team-create-btn / team-join-btn    (CLI-only stubs)
//   - team-name-edit-btn / save / cancel
//   - team-copy-invite
//   - team-generate-invite / team-generate-new-invite
//   - team-revoke-invites
//   - data-team-remove-user
//   - data-team-edit-profile  (opens openMemberProfileEditor)
//   - data-team-change-role
//   - data-team-remove (legacy laptop-level)
//   - data-team-rename + member-save-name / member-cancel-name
//   - data-team-toggle-role (legacy laptop-level)
//   - team-delete-btn
//
// Bridge dependencies: getSupabase + state.token (for the invite-send
// path that prefers a Supabase JWT so invited_by_email reflects the
// actual admin; falls back to the team token for CLI-only admins).
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2i).
import { $ } from "./dom.js";
import { escapeHTML } from "./util.js";
import { showToast, showConfirm } from "./ui.js";
import { api, apiWithMessage, AuthRedirectError } from "./api.js";
import { state } from "./state.js";
import { teamState } from "./page-state.js";
import { navigate } from "./router.js";
import { renderTeamPage } from "./pages/team.js";
import { renderTeamPageInner } from "./team-page.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

// focusInviteFromURL handles the ?invite=1 deep link (used by `agc setup`'s
// "Invite teammates" step): scroll to the email-invite form and put the
// cursor in it. Only acts when the form is actually shown (admins on the
// hosted dashboard); otherwise the page renders as usual.
function focusInviteFromURL() {
  let params;
  try { params = new URLSearchParams(window.location.search); } catch { return; }
  if (params.get('invite') !== '1') return;
  const panel = document.getElementById('team-invite-v2-panel');
  const box = document.getElementById('team-invite-emails');
  if (!panel || !box || !panel.offsetParent) return;
  panel.scrollIntoView({ block: 'center', behavior: 'smooth' });
  box.focus({ preventScroll: true });
}

export async function renderTeamV2(isAdmin) {
  // Seat counter — uses billing status. Non-admins see it too (read-only).
  // v0.32.6: hide the counter entirely in solo mode. Solo's "1 of 1
  // seats" trips the pct>=1 danger-red branch below, making the
  // counter read as a problem when in fact the user is using it
  // exactly as intended. The seat concept only matters for paid
  // tiers — solo just always has its one machine.
  const localDaemonModeTeam =
    window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
  try {
    const billing = await api('/v1/billing/status');
    const seatEl = document.getElementById('team-seat-counter');
    if (seatEl && billing) {
      if (localDaemonModeTeam || billing.tier === "solo") {
        seatEl.style.display = "none";
      } else {
        seatEl.style.display = "";
        const cap = billing.tier === 'enterprise' ? '∞' : (billing.max_members || (billing.tier === 'startup' ? 10 : 1));
        const used = billing.member_count || 0;
        seatEl.textContent = used + ' of ' + cap + ' seats';
        const capNum = billing.max_members || (billing.tier === 'startup' ? 10 : billing.tier === 'enterprise' ? 999999 : 1);
        const pct = used / capNum;
        if (pct >= 1) { seatEl.style.background = 'var(--danger-bg, rgba(248,113,113,0.15))'; seatEl.style.color = 'var(--danger)'; }
        else if (pct >= 0.8) { seatEl.style.background = 'var(--warning-bg, rgba(251,191,36,0.15))'; seatEl.style.color = 'var(--warning)'; }
        else { seatEl.style.background = 'rgba(52,211,153,0.12)'; seatEl.style.color = 'var(--success)'; }
      }
    }
  } catch {}

  // Invite form visibility: members see a read-only message
  const invPanel = document.getElementById('team-invite-v2-panel');
  if (invPanel) {
    const body = invPanel.querySelector('.panel-body');
    if (!isAdmin) {
      body.innerHTML = '<div style="padding: var(--s3) 0; color: var(--text-secondary); font-size: 13px;">Only admins can invite new teammates.</div>';
    }
  }

  // Load + render pending invitations (admins only; members don't see this panel)
  const pendingPanel = document.getElementById('team-pending-panel');
  if (!isAdmin) {
    if (pendingPanel) pendingPanel.style.display = 'none';
    return;
  }
  try {
    const data = await api('/v1/teams/invitations');
    const pending = (data.invitations || []).filter((i) => i.status === 'pending' && new Date(i.expires_at) > new Date());
    const list = document.getElementById('team-pending-list');
    const countEl = document.getElementById('team-pending-count');
    if (pending.length === 0) {
      pendingPanel.style.display = 'none';
      return;
    }
    pendingPanel.style.display = '';
    if (countEl) countEl.textContent = pending.length + ' pending';
    list.innerHTML = pending.map((inv) => {
      const expiresIn = Math.max(0, Math.ceil((new Date(inv.expires_at) - new Date()) / 86400000));
      return `<div style="display: flex; align-items: center; justify-content: space-between; padding: 12px var(--s4); border-bottom: 1px solid var(--border); gap: var(--s3);">
        <div style="min-width: 0; flex: 1;">
          <div style="color: var(--text); font-size: 14px; font-weight: 500;">${escapeHTML(inv.email)}</div>
          <div style="color: var(--text-tertiary); font-size: 12px; margin-top: 2px;">
            ${escapeHTML(inv.role || 'member')} &middot; expires in ${expiresIn} day${expiresIn === 1 ? '' : 's'} &middot; invited ${inv.invited_by_email ? 'by ' + escapeHTML(inv.invited_by_email) : ''}
          </div>
        </div>
        <div style="display: flex; gap: var(--s2);">
          <button class="btn btn-sm" data-inv-resend="${escapeHTML(inv.id)}" style="font-size: 11px; padding: 3px 10px;">Resend</button>
          <button class="btn btn-sm" data-inv-revoke="${escapeHTML(inv.id)}" style="font-size: 11px; padding: 3px 10px; color: var(--danger); border-color: var(--danger);">Revoke</button>
        </div>
      </div>`;
    }).join('');
  } catch (e) {
    console.warn('pending invitations fetch failed:', e);
  }
  focusInviteFromURL();
}

// openMemberProfileEditor: lightweight inline editor for a team
// member's title + seniority. Reuses the confirm modal as a generic
// prompt host so we don't have to build a new dedicated dialog.
// PATCH-es /v1/teams/members/by-user/:id with whichever fields
// changed; the Worker's handler accepts partial payloads.
function openMemberProfileEditor(userId, email, curTitle, curSeniority) {
  const SENIORITIES = [
    { value: '', label: '— Unset —' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid' },
    { value: 'senior', label: 'Senior' },
    { value: 'principal', label: 'Principal' },
  ];
  const senOpts = SENIORITIES.map(s =>
    '<option value="' + escapeHTML(s.value) + '"' +
    (s.value === curSeniority ? ' selected' : '') +
    '>' + escapeHTML(s.label) + '</option>'
  ).join('');
  const body =
    '<div style="display: grid; gap: 14px; text-align: left;">' +
      '<div style="font-size: 13px; color: var(--text-secondary);">Editing profile for <strong style="color: var(--text);">' + escapeHTML(email) + '</strong></div>' +
      '<div>' +
        '<label for="member-profile-title" style="display: block; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em; margin-bottom: 4px; font-weight: 600;">Title</label>' +
        '<input id="member-profile-title" type="text" placeholder="e.g. Staff Engineer" value="' + escapeHTML(curTitle) + '" maxlength="120" style="width: 100%; padding: 8px 10px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-panel-solid); color: var(--text); font-size: 13.5px; box-sizing: border-box;">' +
      '</div>' +
      '<div>' +
        '<label for="member-profile-seniority" style="display: block; font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.05em; margin-bottom: 4px; font-weight: 600;">Seniority</label>' +
        '<select id="member-profile-seniority" style="width: 100%; padding: 8px 10px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-panel-solid); color: var(--text); font-size: 13.5px; box-sizing: border-box;">' + senOpts + '</select>' +
        '<div style="font-size: 11px; color: var(--text-tertiary); margin-top: 4px;">Policy rules can target seniority levels (e.g. "junior/mid agents need senior approval on /payments/**").</div>' +
      '</div>' +
    '</div>';
  showConfirm('Member profile', body, 'Save').then(async (ok) => {
    if (!ok) return;
    const newTitle = (document.getElementById('member-profile-title')?.value || '').trim();
    const newSeniority = (document.getElementById('member-profile-seniority')?.value || '').trim();
    const patch = {};
    if (newTitle !== curTitle) patch.title = newTitle || null;
    if (newSeniority !== curSeniority) patch.seniority = newSeniority || null;
    if (Object.keys(patch).length === 0) return;
    try {
      await apiWithMessage('/v1/teams/members/by-user/' + encodeURIComponent(userId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      teamState.lastFetch = 0;
      renderTeamPage();
      showToast('success', 'Profile updated.');
    } catch (err) {
      // Swallow auth-redirect: api.js already flipped to the login
      // screen. Toasting "Save failed: unauthorized" on top of the
      // login form is confusing noise.
      if (err instanceof AuthRedirectError) return;
      showToast('error', 'Save failed: ' + (err.message || err));
    }
  });
}

// ---- module init: delegated click handlers ----

function wireListeners() {
  // Email-invite submit + per-invite resend/revoke + CLI invite collapse
  document.addEventListener('click', async (e) => {
    if (e.target.id === 'team-invite-send') {
      const textarea = document.getElementById('team-invite-emails');
      const roleEl = document.getElementById('team-invite-role');
      const seniorityEl = document.getElementById('team-invite-seniority');
      const titleEl = document.getElementById('team-invite-title');
      const resultEl = document.getElementById('team-invite-v2-result');
      if (!textarea || !roleEl || !resultEl) return;
      const emails = textarea.value.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      if (emails.length === 0) {
        resultEl.innerHTML = '<span style="color: var(--danger);">Enter at least one email address.</span>';
        return;
      }
      const role = roleEl.value;
      const seniority = (seniorityEl && seniorityEl.value || '').trim() || undefined;
      const title = (titleEl && titleEl.value || '').trim() || undefined;
      e.target.disabled = true;
      e.target.textContent = 'Sending...';

      // Direct fetch so we can inspect the 402 body for seat-cap details.
      // Prefer the Supabase JWT when available so invited_by_email reflects
      // the actual admin; fall back to the team token for CLI-only admins.
      let authHeader = 'Bearer ' + state.token;
      try {
        const supabase = getSupabase();
        if (supabase) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) authHeader = 'Bearer ' + session.access_token;
        }
      } catch {}
      try {
        const r = await fetch(COORDINATOR + '/v1/teams/invitations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader,
          },
          body: JSON.stringify({ emails, role, seniority, title }),
        });
        const data = await r.json().catch(() => ({}));

        if (r.status === 402 && data.error === 'seat_cap_exceeded') {
          const planName = data.current_plan === 'solo' ? 'Solo' : data.current_plan === 'startup' ? 'Startup' : 'Enterprise';
          const needed = Math.max(data.adding || 0, 1);
          // Two distinct denial cases:
          //   "buy_more_seats" — paid tier, just need to bump quantity.
          //                      CTA opens Stripe portal in update-quantity mode.
          //   "upgrade_tier"   — solo, no team coordinator at all.
          //                      CTA goes through Stripe Checkout.
          const action = data.action || (data.current_plan === 'solo' ? 'upgrade_tier' : 'buy_more_seats');

          if (action === 'buy_more_seats') {
            const seatsNeeded = data.seats_needed || needed;
            const newTotal = (data.cap || 0) + seatsNeeded;
            resultEl.innerHTML = `<div style="background: var(--bg-elevated); border: 1px solid var(--warning); border-radius: var(--r-md); padding: var(--s4); margin-top: var(--s2);">
              <div style="color: var(--warning); font-weight: 600; margin-bottom: 6px;">You've used all your paid seats</div>
              <div style="color: var(--text-secondary); font-size: 13px; line-height: 1.6; margin-bottom: var(--s3);">
                Your <strong>${planName}</strong> plan is paid for <strong>${data.cap} seat${data.cap === 1 ? '' : 's'}</strong> — currently using ${data.used}${data.pending ? ' + ' + data.pending + ' pending' : ''}.
                <br/>Buy <strong>${seatsNeeded} more seat${seatsNeeded === 1 ? '' : 's'}</strong> (new total: ${newTotal}) to invite ${needed === 1 ? 'this teammate' : 'these teammates'}.
                <br/><span style="color: var(--text-tertiary); font-size: 11px;">Stripe will charge prorated for the rest of this billing period.</span>
              </div>
              <button class="btn btn-sm btn-primary" id="team-invite-buy-seats-cta">Buy more seats</button>
              <button class="btn btn-sm" id="team-invite-cancel" style="margin-left: 8px;">Cancel</button>
            </div>`;
            document.getElementById('team-invite-buy-seats-cta').onclick = async () => {
              // Save the intent so after the Stripe portal round-trip we come back and send the invites.
              try {
                sessionStorage.setItem('ac-pending-invites', JSON.stringify({ emails, role, savedAt: Date.now() }));
              } catch {}
              try {
                const cta = document.getElementById('team-invite-buy-seats-cta');
                cta.disabled = true; cta.textContent = 'Opening Stripe...';
                const portal = await api('/v1/billing/portal', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ flow: 'update_quantity' }),
                });
                if (portal.url) {
                  location.href = portal.url;
                } else {
                  cta.disabled = false; cta.textContent = 'Buy more seats';
                  showToast('error', 'Could not open Stripe portal. Try again from the Billing tab.');
                }
              } catch (e2) {
                showToast('error', 'Buy seats failed: ' + (e2.message || 'unknown'));
                const cta = document.getElementById('team-invite-buy-seats-cta');
                if (cta) { cta.disabled = false; cta.textContent = 'Buy more seats'; }
              }
            };
            document.getElementById('team-invite-cancel').onclick = () => { resultEl.innerHTML = ''; };
            return;
          }

          // upgrade_tier branch — solo customer needs to subscribe.
          const upgradeTier = data.upgrade_to || 'startup';
          const upgradeLabel = upgradeTier === 'enterprise' ? 'Enterprise ($25/user/mo, 5 active machines per user, SSO + SLA)' : 'Startup ($8/user/mo, 2 active machines per user)';
          resultEl.innerHTML = `<div style="background: var(--bg-elevated); border: 1px solid var(--warning); border-radius: var(--r-md); padding: var(--s4); margin-top: var(--s2);">
            <div style="color: var(--warning); font-weight: 600; margin-bottom: 6px;">Upgrade to invite teammates</div>
            <div style="color: var(--text-secondary); font-size: 13px; line-height: 1.6; margin-bottom: var(--s3);">
              You're on <strong>Solo</strong>, which doesn't include a hosted team.
              <br/>Upgrade to <strong>${upgradeLabel}</strong> to invite ${needed === 1 ? 'this teammate' : 'these teammates'}.
            </div>
            <button class="btn btn-sm btn-primary" id="team-invite-upgrade-cta">Upgrade and invite</button>
            <button class="btn btn-sm" id="team-invite-upgrade-later" style="margin-left: 8px;">View plans</button>
          </div>`;
          document.getElementById('team-invite-upgrade-cta').onclick = async () => {
            try {
              sessionStorage.setItem('ac-pending-invites', JSON.stringify({ emails, role, savedAt: Date.now() }));
            } catch {}
            // Solo→paid: checkout quantity = current members + pending + new emails.
            const qty = (data.used || 1) + (data.pending || 0) + needed;
            try {
              const cta = document.getElementById('team-invite-upgrade-cta');
              cta.disabled = true; cta.textContent = 'Opening checkout...';
              const checkout = await api('/v1/billing/checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tier: upgradeTier, quantity: qty }),
              });
              if (checkout.url) {
                location.href = checkout.url;
              } else {
                cta.disabled = false; cta.textContent = 'Upgrade and invite';
                showToast('error', 'Could not start checkout. Try again from the Billing tab.');
              }
            } catch (e2) {
              showToast('error', 'Upgrade failed: ' + (e2.message || 'unknown'));
              const cta = document.getElementById('team-invite-upgrade-cta');
              if (cta) { cta.disabled = false; cta.textContent = 'Upgrade and invite'; }
            }
          };
          // IA arc B' (2026-05-27): Billing panel moved from Settings/Daemon to Account.
          document.getElementById('team-invite-upgrade-later').onclick = () => navigate('account');
          return;
        }

        if (!r.ok) {
          resultEl.innerHTML = '<span style="color: var(--danger);">' + escapeHTML(data.message || data.error || 'Failed to send (HTTP ' + r.status + ')') + '</span>';
          return;
        }

        textarea.value = '';
        const invitations = data.invitations || [];
        const count = invitations.length;
        const failed = invitations.filter((i) => !i.email_sent);
        const sentOK = count - failed.length;

        if (failed.length === 0) {
          // Happy path — all emails delivered.
          resultEl.innerHTML = '<span style="color: var(--success);">✓ Sent ' + count + ' invitation' + (count === 1 ? '' : 's') + '.</span>';
          setTimeout(() => { if (resultEl) resultEl.innerHTML = ''; }, 4000);
        } else {
          // Partial failure — invitations exist (recipients can still claim them
          // if you give them the link manually) but the email didn't go out.
          // Show a warning, list which addresses failed, and offer "Resend all".
          // The invitations are visible in the Pending Invitations list below
          // with their own per-row Resend button as a fallback.
          const failedEmails = failed.map((i) => i.email).filter(Boolean);
          const list = failedEmails.map(escapeHTML).join(', ');
          resultEl.innerHTML =
            '<div style="color: var(--warning); padding: 10px 12px; background: rgba(251, 191, 36, 0.08); border: 1px solid rgba(251, 191, 36, 0.4); border-radius: 6px; line-height: 1.45;">' +
              '<strong>' + sentOK + ' of ' + count + ' email' + (count === 1 ? '' : 's') + ' delivered.</strong> ' +
              'Invitation' + (failed.length === 1 ? '' : 's') + ' for <code style="font-size: 12px;">' + list + '</code> ' +
              ' ' + (failed.length === 1 ? 'was created but the email failed to send' : 'were created but the emails failed to send') + '. ' +
              'Use the Pending Invitations list below to resend, or copy the invite links manually.' +
            '</div>';
          // Don't auto-clear — the user needs to read this.
          showToast('error', sentOK + '/' + count + ' invitation emails delivered. Check the warning for details.');
        }
        teamState.lastFetch = 0;
        renderTeamPage();
      } catch (err) {
        resultEl.innerHTML = '<span style="color: var(--danger);">' + escapeHTML((err && err.message) || 'Network error') + '</span>';
      } finally {
        e.target.disabled = false;
        e.target.textContent = 'Send invitations';
      }
      return;
    }
    if (e.target.dataset && e.target.dataset.invResend) {
      const id = e.target.dataset.invResend;
      e.target.disabled = true;
      e.target.textContent = '...';
      try {
        await apiWithMessage('/v1/teams/invitations/' + id + '/resend', { method: 'POST' });
        e.target.textContent = 'Resent';
        setTimeout(() => { e.target.textContent = 'Resend'; e.target.disabled = false; }, 2000);
      } catch (err) {
        e.target.textContent = 'Resend';
        e.target.disabled = false;
        showToast('error', 'Resend failed: ' + (err.message || 'unknown'));
      }
      return;
    }
    if (e.target.dataset && e.target.dataset.invRevoke) {
      const id = e.target.dataset.invRevoke;
      const ok = await showConfirm(
        'Revoke this invitation?',
        'The accept link will stop working immediately.',
        'Revoke'
      );
      if (!ok) return;
      try {
        await apiWithMessage('/v1/teams/invitations/' + id + '/revoke', { method: 'POST' });
        teamState.lastFetch = 0;
        renderTeamPage();
        showToast('success', 'Invitation revoked.');
      } catch (err) {
        showToast('error', 'Revoke failed: ' + (err.message || 'unknown'));
      }
      return;
    }
    // CLI invite section collapse toggle
    if (e.target.closest && e.target.closest('#team-cli-invite-header')) {
      const body = document.getElementById('team-invite-section');
      const caret = document.getElementById('team-cli-invite-caret');
      if (body && caret) {
        const open = body.style.display !== 'none';
        body.style.display = open ? 'none' : '';
        caret.innerHTML = open ? '&#9656;' : '&#9662;';
      }
    }
  });

  // Team page event handlers (legacy + identity-level)
  document.addEventListener('click', (e) => {
    // Show create form
    if (e.target.id === 'team-show-create') {
      const cf = $('team-create-form');
      const jf = $('team-join-form');
      cf.style.display = cf.style.display === 'none' ? '' : 'none';
      jf.style.display = 'none';
      return;
    }
    // Show join form
    if (e.target.id === 'team-show-join') {
      const jf = $('team-join-form');
      const cf = $('team-create-form');
      jf.style.display = jf.style.display === 'none' ? '' : 'none';
      cf.style.display = 'none';
      return;
    }
    // Create team (disabled — use `agc team create` locally)
    if (e.target.id === 'team-create-btn') {
      $('team-create-result').innerHTML = '<span style="color: var(--text-secondary);">Team creation is done via the CLI: <code>agc team create</code></span>';
      return;
    }
    // Join team (disabled — use `agc team join` locally)
    if (e.target.id === 'team-join-btn') {
      $('team-join-result').innerHTML = '<span style="color: var(--text-secondary);">Team joining is done via the CLI: <code>agc team join &lt;code&gt;</code></span>';
      return;
    }
    // Rename team — show edit mode
    if (e.target.id === 'team-name-edit-btn') {
      teamState.editing = true;
      $('team-name-display').style.display = 'none';
      $('team-name-edit-mode').style.display = 'flex';
      const input = $('team-name-input');
      input.value = $('team-name-text').textContent;
      input.focus();
      input.select();
      return;
    }
    // Save team name
    if (e.target.id === 'team-name-save') {
      const newName = ($('team-name-input').value || '').trim();
      if (!newName) return;
      e.target.disabled = true;
      apiWithMessage('/v1/teams/name', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      }).then(() => { teamState.editing = false; teamState.lastFetch = 0; renderTeamPage(); showToast('success', 'Team name updated.'); })
        .catch((err) => { showToast('error', 'Failed: ' + (err.message || 'unknown error')); })
        .finally(() => { e.target.disabled = false; });
      return;
    }
    // Cancel team name edit
    if (e.target.id === 'team-name-cancel') {
      teamState.editing = false;
      $('team-name-display').style.display = '';
      $('team-name-edit-mode').style.display = 'none';
      return;
    }
    // Copy invite
    if (e.target.id === 'team-copy-invite') {
      const text = $('team-invite-display').textContent;
      navigator.clipboard.writeText(text).then(() => {
        e.target.textContent = 'Copied!';
        setTimeout(() => { e.target.textContent = 'Copy'; }, 1500);
      });
      return;
    }
    // Generate new invite
    if (e.target.id === 'team-generate-invite' || e.target.id === 'team-generate-new-invite') {
      // Show loading state
      $('team-invite-no-code').style.display = 'none';
      $('team-invite-has-code').style.display = 'none';
      $('team-invite-loading').style.display = '';
      apiWithMessage('/v1/teams/invite', { method: 'POST' }).then(data => {
        if (data.invite_code) {
          teamState.inviteCode = data.invite_code;
          teamState.lastFetch = 0;
          renderTeamPage();
        }
      }).catch((err) => {
        showToast('error', 'Failed: ' + (err.message || 'unknown error'));
        teamState.lastFetch = 0;
        renderTeamPage();
      });
      return;
    }
    // Revoke all invite codes
    if (e.target.id === 'team-revoke-invites') {
      (async () => {
        const ok = await showConfirm(
          'Revoke invite code',
          'This invite code will stop working immediately. Anyone who hasn\'t joined yet will need a new code.',
          'Revoke'
        );
        if (!ok) return;
        try {
          await apiWithMessage('/v1/teams/invites', { method: 'DELETE' });
          teamState.inviteCode = null;
          teamState.lastFetch = 0;
          renderTeamPage();
          showToast('success', 'Invite code revoked.');
        } catch (err) { showToast('error', 'Failed: ' + (err.message || 'unknown error')); }
      })();
      return;
    }
    // Remove user (identity-level, v2) — revokes all their machines
    const removeUserBtn = e.target.closest('[data-team-remove-user]');
    if (removeUserBtn) {
      const userId = removeUserBtn.dataset.teamRemoveUser;
      const email = removeUserBtn.dataset.email || userId;
      (async () => {
        const ok = await showConfirm(
          'Remove ' + escapeHTML(email) + '?',
          'This will revoke all their machines and remove them from the team. Their past audit log entries stay.<br><br><strong>This cannot be undone.</strong>',
          'Remove'
        );
        if (!ok) return;
        try {
          await apiWithMessage('/v1/teams/members/by-user/' + encodeURIComponent(userId), { method: 'DELETE' });
          teamState.lastFetch = 0;
          renderTeamPage();
          showToast('success', 'Member removed.');
        } catch (err) {
          showToast('error', 'Failed to remove: ' + (err.message || 'unknown'));
        }
      })();
      return;
    }
    // Edit profile (title + seniority). Admin-only. Opens a small
    // inline prompt; saves via PATCH /v1/teams/members/by-user/:id.
    const editProfileBtn = e.target.closest('[data-team-edit-profile]');
    if (editProfileBtn) {
      const userId = editProfileBtn.dataset.teamEditProfile;
      const email = editProfileBtn.dataset.email || '';
      const curTitle = editProfileBtn.dataset.title || '';
      const curSeniority = editProfileBtn.dataset.seniority || '';
      openMemberProfileEditor(userId, email, curTitle, curSeniority);
      return;
    }
    // Change role (identity-level, v2)
    const changeRoleBtn = e.target.closest('[data-team-change-role]');
    if (changeRoleBtn) {
      const userId = changeRoleBtn.dataset.teamChangeRole;
      const currentRole = changeRoleBtn.dataset.currentRole;
      const newRole = currentRole === 'admin' ? 'member' : 'admin';
      const memberRow = changeRoleBtn.closest('[data-member-name]');
      const memberName = (memberRow && memberRow.dataset.memberName) || 'this member';
      (async () => {
        const verb = newRole === 'admin' ? 'Promote' : 'Demote';
        const explainer = newRole === 'admin'
          ? 'They will gain admin powers: invite + remove members, change roles, manage billing, delete the team.'
          : 'They will lose admin powers and become a regular member.';
        const ok = await showConfirm(
          verb + ' ' + memberName + '?',
          explainer,
          verb
        );
        if (!ok) return;
        const prev = changeRoleBtn.textContent;
        changeRoleBtn.disabled = true; changeRoleBtn.textContent = '…';
        try {
          await apiWithMessage('/v1/teams/members/by-user/' + encodeURIComponent(userId), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ role: newRole }),
          });
          teamState.lastFetch = 0;
          renderTeamPage();
          showToast('success', verb.endsWith('e') ? verb + 'd.' : verb + 'ed.');
        } catch (err) {
          changeRoleBtn.disabled = false; changeRoleBtn.textContent = prev;
          showToast('error', 'Failed to change role: ' + (err.message || 'unknown'));
        }
      })();
      return;
    }

    // backfill-owner + transfer-ownership handlers removed in migration
    // 035 — the Owner role no longer exists; any admin can perform what
    // used to be owner-only operations.

    // Remove member (legacy laptop-level; also used by per-machine revoke in people view)
    const removeBtn = e.target.closest('[data-team-remove]');
    if (removeBtn) {
      const lid = removeBtn.dataset.teamRemove;
      const computer = removeBtn.dataset.revokeComputer;
      const name = computer || removeBtn.closest('[style]')?.querySelector('[style*="font-weight: 500"]')?.textContent || lid;
      (async () => {
        const ok = computer !== undefined
          ? await showConfirm(
              'Revoke computer',
              'Revoke <strong>' + escapeHTML(name) + '</strong> (' + escapeHTML(lid) + ')?<br>It will no longer be able to connect to the team.',
              'Revoke'
            )
          : await showConfirm(
              'Remove member',
              'Remove <strong>' + escapeHTML(name) + '</strong> (' + escapeHTML(lid) + ') from the team?<br>Their token will be revoked.',
              'Remove'
            );
        if (!ok) return;
        try {
          await apiWithMessage('/v1/teams/members/' + encodeURIComponent(lid), { method: 'DELETE' });
          teamState.lastFetch = 0;
          renderTeamPage();
          showToast('success', computer !== undefined ? 'Computer revoked.' : 'Member removed.');
        } catch (err) {
          showToast('error', 'Failed to remove member: ' + (err.message || 'unknown error'));
        }
      })();
      return;
    }
    // Rename member — show inline input with save/cancel
    const renameBtn = e.target.closest('[data-team-rename]');
    if (renameBtn) {
      teamState.editing = true;
      const row = renameBtn.closest('[data-member-row]');
      if (!row) return;
      row.querySelector('[data-member-name-display]').style.display = 'none';
      const editWrap = row.querySelector('[data-member-name-edit]');
      editWrap.style.display = 'flex';
      const input = editWrap.querySelector('input');
      input.value = renameBtn.dataset.currentName;
      input.focus();
      input.select();
      return;
    }
    // Save member name
    const saveName = e.target.closest('[data-member-save-name]');
    if (saveName) {
      const lid = saveName.dataset.memberSaveName;
      const row = saveName.closest('[data-member-row]');
      const input = row.querySelector('[data-member-name-edit] input');
      const newName = (input.value || '').trim();
      if (!newName) return;
      saveName.disabled = true;
      apiWithMessage('/v1/teams/members/' + encodeURIComponent(lid) + '/name', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      }).then(() => { teamState.editing = false; teamState.lastFetch = 0; renderTeamPage(); showToast('success', 'Name updated.'); })
        .catch((err) => { showToast('error', 'Failed: ' + (err.message || 'unknown error')); })
        .finally(() => { saveName.disabled = false; });
      return;
    }
    // Cancel member name edit
    const cancelName = e.target.closest('[data-member-cancel-name]');
    if (cancelName) {
      teamState.editing = false;
      const row = cancelName.closest('[data-member-row]');
      row.querySelector('[data-member-name-display]').style.display = '';
      row.querySelector('[data-member-name-edit]').style.display = 'none';
      return;
    }
    // Toggle role (legacy laptop-level)
    const roleBtn = e.target.closest('[data-team-toggle-role]');
    if (roleBtn) {
      const lid = roleBtn.dataset.teamToggleRole;
      const currentRole = roleBtn.dataset.currentRole;
      const newRole = currentRole === 'admin' ? 'laptop' : 'admin';
      const label = newRole === 'admin' ? 'Promote to admin' : 'Demote to member';
      const desc = newRole === 'admin'
        ? 'This gives <strong>' + escapeHTML(lid) + '</strong> full admin access: invite, remove, promote/demote other members, and delete the team.'
        : 'This removes admin access from <strong>' + escapeHTML(lid) + '</strong>. They will only be able to view team status and members.';
      (async () => {
        const ok = await showConfirm(label, desc, label);
        if (!ok) return;
        try {
          await apiWithMessage('/v1/teams/members/' + encodeURIComponent(lid) + '/role', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ role: newRole }),
          });
          teamState.lastFetch = 0;
          renderTeamPage();
          showToast('success', 'Role updated.');
        } catch (err) { showToast('error', 'Failed: ' + (err.message || 'unknown error')); }
      })();
      return;
    }
    // Delete team
    if (e.target.id === 'team-delete-btn') {
      (async () => {
        const ok = await showConfirm(
          'Delete team',
          'This permanently removes the team and all member data.<br><strong>This cannot be undone.</strong>',
          'Delete Team'
        );
        if (!ok) return;
        e.target.disabled = true;
        e.target.textContent = 'Deleting...';
        try {
          await apiWithMessage('/v1/teams/mine', { method: 'DELETE' });
          teamState.info = null;
          teamState.members = [];
          teamState.inviteCode = null;
          teamState.lastFetch = 0;
          renderTeamPageInner();
          // Clear form inputs so old text doesn't persist
          const nameInput = $('team-create-name');
          if (nameInput) nameInput.value = '';
          const createResult = $('team-create-result');
          if (createResult) createResult.innerHTML = '';
          const joinResult = $('team-join-result');
          if (joinResult) joinResult.innerHTML = '';
          const joinCode = $('team-join-code');
          if (joinCode) joinCode.value = '';
          // Hide both forms
          const cf = $('team-create-form');
          const jf = $('team-join-form');
          if (cf) cf.style.display = 'none';
          if (jf) jf.style.display = 'none';
        } catch (err) {
          showToast('error', 'Failed to delete team: ' + (err.message || 'unknown error'));
        } finally {
          e.target.disabled = false;
          e.target.textContent = 'Delete Team';
        }
      })();
      return;
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
